
import { ref } from 'vue'

import IconLink from 'vue-material-design-icons/Link.vue'
import IconStar from 'vue-material-design-icons/Star.vue'
import IconVideo from 'vue-material-design-icons/Video.vue'
import IconWeb from 'vue-material-design-icons/Web.vue'

import { t } from '@nextcloud/l10n'

import NcAvatar from '@nextcloud/vue/components/NcAvatar'
import { useIsDarkTheme } from '@nextcloud/vue/composables/useIsDarkTheme'

import { AVATAR, CONVERSATION } from '../constants.ts'
import { getConversationAvatarOcsUrl } from '../services/avatarService.ts'
import { hasTalkFeature } from '../services/CapabilitiesManager.ts'
import { getPreloadedUserStatus } from '../utils/userStatus.ts'

const supportsAvatar = hasTalkFeature('local', 'avatar')

export default {
	name: 'ConversationIcon',

	components: {
		IconStar,
		IconVideo,
		NcAvatar,
	},

	props: {
		/**
		 * Allow to hide the favorite icon, e.g. on mentions
		 */
		hideFavorite: {
			type: Boolean,
			default: true,
		},

		hideCall: {
			type: Boolean,
			default: true,
		},

		disableMenu: {
			type: Boolean,
			default: true,
		},

		hideUserStatus: {
			type: Boolean,
			default: false,
		},

		showUserOnlineStatus: {
			type: Boolean,
			default: false,
		},

		item: {
			type: Object,
			default() {
				return {
					objectType: '',
					type: 0,
					displayName: '',
					isFavorite: false,
				}
			},
		},

		/**
		 * Reduces the opacity of the icon if true
		 */
		offline: {
			type: Boolean,
			default: false,
		},

		size: {
			type: Number,
			default: AVATAR.SIZE.DEFAULT,
		},
	},

	setup() {
		const isDarkTheme = useIsDarkTheme()

		const failed = ref(false)

		/**
		 * If avatar image failed to load, toggle value to provide a fallback
		 */
		function onError() {
			failed.value = true
		}

		return {
			isDarkTheme,
			failed,
			onError,
		}
	},

	computed: {
		showCall() {
			return !this.hideCall && this.item.hasCall
		},

		showFavorite() {
			return !this.hideFavorite && this.item.isFavorite
		},

		preloadedUserStatus() {
			if (this.hideUserStatus) {
				return undefined
			}

			return getPreloadedUserStatus(this.item)
		},

		iconClass() {
			if (this.item.isDummyConversation) {
				// Prevent a 404 when trying to load an avatar before the conversation data is actually loaded
				return this.item.type === CONVERSATION.TYPE.PUBLIC ? 'icon-public' : 'icon-contacts'
			}

			if (!supportsAvatar || this.failed) {
				if (this.item.objectType === CONVERSATION.OBJECT_TYPE.FILE
					|| this.item.type === CONVERSATION.TYPE.NOTE_TO_SELF) {
					return 'icon-file'
				} else if (this.item.objectType === CONVERSATION.OBJECT_TYPE.VIDEO_VERIFICATION) {
					return 'icon-password'
				} else if (this.item.objectType === CONVERSATION.OBJECT_TYPE.EMAIL) {
					return 'icon-mail'
				} else if (this.item.objectType === CONVERSATION.OBJECT_TYPE.PHONE) {
					return 'icon-phone'
				} else if (this.item.objectType === CONVERSATION.OBJECT_TYPE.CIRCLES) {
					return 'icon-team'
				} else if (this.item.type === CONVERSATION.TYPE.CHANGELOG) {
					return 'icon-changelog'
				} else if (this.item.type === CONVERSATION.TYPE.ONE_TO_ONE_FORMER) {
					return 'icon-user'
				} else if (this.item.type === CONVERSATION.TYPE.GROUP) {
					return 'icon-contacts'
				} else if (this.item.type === CONVERSATION.TYPE.PUBLIC) {
					return 'icon-public'
				}
				return undefined
			}

			if (this.item.token) {
				// Existing conversations use the /avatar endpoint… Always!
				return undefined
			}

			if (this.item.objectType === CONVERSATION.OBJECT_TYPE.CIRCLES) {
				// Team icon for group conversation suggestions
				return 'icon-team'
			}

			if (this.item.type === CONVERSATION.TYPE.GROUP) {
				// Group icon for group conversation suggestions
				return 'icon-contacts'
			}

			// Fall-through for other conversation suggestions to user-avatar handling
			return undefined
		},

		themeClass() {
			return `conversation-icon--${this.isDarkTheme ? 'dark' : 'bright'}`
		},

		isOneToOne() {
			return this.item.type === CONVERSATION.TYPE.ONE_TO_ONE
		},

		conversationType() {
			if (this.item.remoteServer) {
				return { key: 'federated', icon: IconWeb, label: t('spreed', 'Federated conversation') }
			} else if (this.item.type === CONVERSATION.TYPE.PUBLIC) {
				return { key: 'public', icon: IconLink, label: t('spreed', 'Public conversation') }
			}
			return null
		},

		avatarUrl() {
			if (!supportsAvatar || this.item.isDummyConversation) {
				return undefined
			}

			return getConversationAvatarOcsUrl(this.item.token, this.isDarkTheme, this.item.avatarVersion)
		},
	},

	methods: {
		t,
	},
}
